/**
 * Types and helpers for DataGrail's `window.DG_BANNER_API`.
 *
 * These shapes mirror the API that DataGrail's `consent.js` actually installs.
 * Getting them right matters more than usual: `setConsentPreferences` validates
 * its payload and throws on anything unexpected, and because DataGrail's
 * internal handler is `async` that throw surfaces as an unhandled rejection
 * instead of at the call site. A wrong shape therefore fails silently.
 */

/** DataGrail's category keys, as used in `cookieOptions[].gtm_key`. */
export const DG_CATEGORY_ESSENTIAL = "dg-category-essential";

export interface DGCookieOption {
  gtm_key: string;
  isEnabled: boolean;
}

export interface DGConsentPreferences {
  /** British spelling is intentional: it is what DataGrail's validator checks. */
  isCustomised: boolean;
  cookieOptions: DGCookieOption[];
}

export interface DGSetConsentPreferencesOptions {
  runPreferenceCallbacks?: boolean;
}

export interface DGBannerApi {
  showConsentBanner: () => void;
  hideConsentBanner: () => void;
  getConsentPreferences: () => DGConsentPreferences;
  setConsentPreferences: (
    prefs: DGConsentPreferences,
    options?: DGSetConsentPreferencesOptions,
  ) => DGConsentPreferences;
  getCategories: () => DGCookieOption[];
  /** Takes a bare category name (e.g. `"performance"`), not the `dg-category-` key. */
  categoryEnabled: (category: string) => boolean;
  /**
   * Optional because it is unconfirmed. The only reload-blocking mechanism this
   * codebase has actually observed is `config().blockReload` read as a property
   * at init, which is what `blockReloadScript` sets. A setter method may not
   * exist, so anything calling this must check callability first rather than
   * trusting the type.
   */
  blockReload?: (blocked: boolean) => void;
  isVisible: () => boolean;
  config: () => { blockReload?: boolean };
}

/** The single argument DataGrail hands to a `preference_callback` handler. */
export interface DGPreferenceCallbackParams {
  action?: string | undefined;
  policyName?: string | undefined;
}

/**
 * Queue DataGrail drains at init, then patches so later pushes are handled too.
 *
 * The two variants are different kinds of thing. `block_reload` carries data.
 * The callback variants carry a *handler* in `params`, so pushing one is how a
 * subscription is registered, and there is no corresponding unregister.
 */
export type DGEventEntry =
  | { event: "block_reload"; block: boolean }
  | {
      event: "preference_callback" | "initial_preference_callback";
      params: (params?: DGPreferenceCallbackParams) => void;
    };

declare global {
  interface Window {
    DG_BANNER_API?: DGBannerApi;
    dgEvent?: DGEventEntry[];
    /** Read once by `consent.js` at init. Must be set before it executes. */
    DG_CONSENT_BLOCK_RELOAD?: boolean;
  }
}

export const getDgBannerApi = (): DGBannerApi | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window.DG_BANNER_API;
};

export const isDataGrailLoaded = (): boolean => getDgBannerApi() !== undefined;

export const showConsentBanner = (): void => {
  getDgBannerApi()?.showConsentBanner();
};

export const hideConsentBanner = (): void => {
  getDgBannerApi()?.hideConsentBanner();
};

/**
 * Denies every optional category, leaving only essentials.
 *
 * Derives the payload from DataGrail's live categories rather than hardcoding
 * names, so it keeps working when the consent container changes.
 */
export const setEssentialOnlyConsent = (): boolean => {
  const api = getDgBannerApi();
  const cookieOptions = api?.getConsentPreferences().cookieOptions;
  if (!api || !cookieOptions?.length) {
    return false;
  }

  api.setConsentPreferences(
    {
      isCustomised: true,
      cookieOptions: cookieOptions.map((option) => ({
        ...option,
        isEnabled: option.gtm_key === DG_CATEGORY_ESSENTIAL,
      })),
    },
    { runPreferenceCallbacks: true },
  );
  return true;
};
